import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts/types';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { FalsePositiveIpList } from '@/components/campaigns/FalsePositiveIpList';
import { SuppressionList } from '@/components/campaigns/SuppressionList';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { MenuItem } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhishingCharacteristicsManagementTab } from './components/PhishingCharacteristicsManagementTab';
import { PhishingSignsManagementTab } from './components/PhishingSignsManagementTab';
import { usePhishingCharacteristicsEditor } from './components/usePhishingCharacteristicsEditor';
import { usePhishingSignsEditor } from './components/usePhishingSignsEditor';

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('globalSettings'),
    url: RedirectPaths[RedirectPathsEnum.PHISHING_GLOBAL_SETTINGS](),
    template: AppBreadCrumbTemplate,
  },
];

export const PhishingGlobalSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const currentAccount = useAppSelector(selectCurrentAccount);

  const {
    signs,
    isLoading: isSignsLoading,
    isChanged: isSignsChanged,
    isSaving: isSignsSaving,
    handleSave: handleSignsSave,
    handleCancel: handleSignsCancel,
    handleEnChange,
    handleDaChange,
  } = usePhishingSignsEditor();

  const {
    characteristics,
    isLoading: isCharacteristicsLoading,
    isChanged: isCharacteristicsChanged,
    isSaving: isCharacteristicsSaving,
    handleSave: handleCharacteristicsSave,
    handleCancel: handleCharacteristicsCancel,
    handleAddRow,
    handleDeleteRow,
    handleChange,
  } = usePhishingCharacteristicsEditor();

  type TabKey =
    | 'suppression-list'
    | 'false-positive-ips'
    | 'phishing-signs'
    | 'phishing-characteristics';

  const tabs = useMemo<Array<{ key: TabKey; label: string }>>(
    () => [
      { key: 'suppression-list', label: t('globalSettings.suppressionList') },
      {
        key: 'phishing-signs',
        label: t('globalSettings.phishingSigns'),
      },
      {
        key: 'phishing-characteristics',
        label: t('globalSettings.phishingCharacteristics'),
      },
      {
        key: 'false-positive-ips',
        label: t('globalSettings.falsePositiveIpList'),
      },
    ],
    [t],
  );

  useEffect(() => {
    setActiveIndex((i) => Math.min(i, tabs.length - 1));
  }, [tabs.length]);

  const tabItems: MenuItem[] = tabs.map(({ label }) => ({ label }));
  const activeKey = tabs[activeIndex]?.key ?? 'suppression-list';

  const activeIsChanged =
    (activeKey === 'phishing-signs' && isSignsChanged) ||
    (activeKey === 'phishing-characteristics' && isCharacteristicsChanged);

  const activeIsSaving =
    (activeKey === 'phishing-signs' && isSignsSaving) ||
    (activeKey === 'phishing-characteristics' && isCharacteristicsSaving);

  const handleActiveSave = () => {
    if (activeKey === 'phishing-signs') return handleSignsSave();
    if (activeKey === 'phishing-characteristics')
      return handleCharacteristicsSave();
  };

  const handleActiveCancel = () => {
    if (activeKey === 'phishing-signs') return handleSignsCancel();
    if (activeKey === 'phishing-characteristics')
      return handleCharacteristicsCancel();
  };

  const showButtons =
    activeKey === 'phishing-signs' || activeKey === 'phishing-characteristics';

  const renderTabContent = () => {
    switch (activeKey) {
      case 'phishing-signs':
        return (
          <PhishingSignsManagementTab
            signs={signs}
            isLoading={isSignsLoading}
            onEnChange={handleEnChange}
            onDaChange={handleDaChange}
          />
        );
      case 'phishing-characteristics':
        return (
          <PhishingCharacteristicsManagementTab
            characteristics={characteristics}
            isLoading={isCharacteristicsLoading}
            isSaving={isCharacteristicsSaving}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            onChange={handleChange}
          />
        );
      case 'false-positive-ips':
        return <FalsePositiveIpList />;
      case 'suppression-list':
      default:
        return <SuppressionList />;
    }
  };

  return (
    <>
      <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />

      <FlexContainer justify="space-between" className="mb-4">
        <h1>{t('globalSettings')}</h1>
        {showButtons && (
          <FlexContainer gap={12} width="auto">
            <AppButton
              label={t('button.save')}
              onClick={handleActiveSave}
              isDisabled={!activeIsChanged}
              state={
                activeIsSaving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
              }
            />
            {activeIsChanged && (
              <AppButton
                type="outlined"
                label={t('globalSettings.cancelChanges')}
                onClick={handleActiveCancel}
                isDisabled={activeIsSaving}
              />
            )}
          </FlexContainer>
        )}
      </FlexContainer>

      <TabMenu
        model={tabItems}
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        className="mb-4"
      />

      {renderTabContent()}
    </>
  );
};
